import {
    callLogin,
    callLogout,
    callOtpRequest,
    fetchPlayerStatus,
} from "@/api-connector/play-digital-api/metods/account/login-api";
import { isErrorDto } from "../utils/user-account-helper";
import { authToken, removeUserCookies, storeUserToken } from "../composables/user-cookies";
import { settings } from "@/settings";
import { cookieStore } from "@/shared/utils/store/use-store";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { PlayerStatusModel } from "@/api-connector/play-digital-api/models/login-models";
import { PlayerDetailModel } from "@/api-connector/play-digital-api/models/account-models";
import { PlayerContactApi } from "@/api-connector/sazka-api/methods/player-contact-api";
import { PlayerContactRequest } from "@/api-connector/sazka-api/models/player-login-request";

const otpSentCookieName = "kasa-otp-sent";
const otpSentCookieDuration = 1 / 24;

export async function playerStatusService(userName: string): Promise<PlayerStatusModel> {
    try {
        const status = await fetchPlayerStatus(userName);

        if (status.blockedSince)
            return Promise.reject(
                "Váš Sazka Klub účet je zablokovaný. Pro odblokování prosím kontaktujte naši infolinku <a href='tel:266121212'>266 12 12 12</a>."
            );

        return status;
    } catch (ex: unknown) {
        if (isErrorDto(ex)) {
            if (ex.code == "Player not found") return Promise.reject("Číslo není správné.");
        }

        return Promise.reject("Chyba při ověření uživatele.");
    }
}

export async function otpRequestService(userName: string, sendAllways = false): Promise<void> {
    if (!sendAllways) {
        const alreadySentUserName = cookieStore.get<string>(otpSentCookieName);

        if (alreadySentUserName == userName) {
            return;
        }
    }

    try {
        await callOtpRequest(userName);
        cookieStore.set(otpSentCookieName, userName, { expires: otpSentCookieDuration });
    } catch (ex: unknown) {
        return Promise.reject("Nepodařilo se odeslat PIN na váš telefon.");
    }
}

export async function otpLoginService(userName: string, pin: string): Promise<string> {
    try {
        const tokens = await callLogin(userName, pin);

        cookieStore.remove(otpSentCookieName);

        const oAuthToken = tokens.find((token) => token.tokenType == "OAuth");
        return oAuthToken.token;
    } catch (ex: unknown) {
        if (isErrorDto(ex)) {
            if (ex.code == "3") return Promise.reject("Pin není správný.");
            else if (ex.code == "10") return Promise.reject("Příliš mnoho neúspěšných pokusů, opakujte akci později.");
        }

        return Promise.reject("Přihlášení se nezdařilo, opakujte akci později.");
    }
}

export async function loginService(userName: string, password: string): Promise<void> {
    try {
        const tokens = await callLogin(userName, password);

        const oAuthToken = tokens.find((token) => token.tokenType == "OAuth");
        storeUserToken(oAuthToken);
    } catch (ex: unknown) {
        if (isErrorDto(ex)) {
            if (ex.code == "3") return Promise.reject("Heslo není správné.");
            else if (ex.code == "10") return Promise.reject("Příliš mnoho neúspěšných pokusů, opakujte akci později.");
        }

        return Promise.reject("Přihlášení se nezdařilo, opakujte akci později.");
    }
}

export async function playerContactService(playerData: PlayerDetailModel) {
    try {
        const playerContactData: PlayerContactRequest = {
            birthdate: playerData.playerPersonalInfo?.birthdate,
            cardId: playerData.cardId,
            cardStatus: playerData.cardStatus,
            clientId: playerData.clientId,
            email: playerData.email,
            firstName: playerData.playerPersonalInfo?.firstName,
            gender: playerData.playerPersonalInfo?.gender,
            lastName: playerData.playerPersonalInfo?.lastName,
            phone: playerData.phone,
        };

        return await PlayerContactApi.callPlayerContact(playerContactData);
    } catch (ex: unknown) {
        console.error(ex);
    }
}

/**
 * Service to logout user. It removes user cookie and calls logout API. Finally, the user is redirected to the homepage.
 * @param {boolean} onlyInternal if true, logout API wont be called
 * @returns {Promise<void>}
 */
export async function logoutService(onlyInternal = false): Promise<void> {
    if (isMobileApp) {
        triggerMobileAppEvent("goToDeeplink", "session-expired");
        return;
    }

    if (!onlyInternal) {
        try {
            await callLogout(authToken.value);
        } catch (ex: unknown) {
            console.error(ex);
        }
    }

    removeUserCookies();

    window.location.href = settings.url.root + window.location.search;
}
